<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>
        Rollekategorier
      </v-toolbar-title>
      <v-spacer />
      <v-menu
        v-model="addMenu"
        left
        :close-on-content-click="false"
        min-width="500"
      >
        <template #activator="{ attrs, on }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            small
          >
            Ny kategori
            <v-icon right> mdi-plus </v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title>
            Ny kategori
          </v-card-title>
          <v-card-text>
            <v-form
              ref="addForm"
              v-model="addForm"
            >
              <v-text-field
                v-model="addItem.title"
                label="Navn"
                autofocus
                :rules="[ v => !!v || 'Udfyld et navn' ]"
                outlined
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="clear"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!addForm"
              :loading="creating"
              @click="create"
            >
              Opret
              <v-icon right> mdi-plus </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-toolbar>
    <v-data-table
      :items="items"
      :headers="headers"
      sort-by="sortOrder"
    >
      <template #[`item.organizational`]="{ value }">
        <v-chip :color="value ? 'success' : 'default'" label small>
          {{ value ? 'Ja' : 'Nej' }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          small
          @click="onEdit(item)"
        >
          <v-icon>mdi-pencil-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger kategori
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-if="editItem"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              :rules="[v => !!v || 'Udfyld titel']"
              outlined
            />
            <v-text-field
              v-model="editItem.sortOrder"
              type="number"
              label="Sortering"
              outlined
              clearable
            />
            <v-checkbox
              v-model="editItem.organizational"
              label="Organisatorisk"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-menu
            width="500"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                color="error"
                text
              >
                Slet
                <v-icon right>
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                Bekræft sletning
              </v-card-title>
              <v-card-text>
                Er du sikker på du vil slette?
              </v-card-text>
              <v-card-actions>
                <v-btn text>
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn
                  :loading="deleting"
                  @click="deleteItem(editItem.id)"
                >
                  Ja, slet
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-spacer />
          <v-btn
            :disabled="!editForm"
            :loading="saving"
            @click="save"
          >
            {{ $t('common.save') }}
            <v-icon right>
              mdi-content-save-outline
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
const baseUrl = 'role-categories';

export default {
  name: 'RoleCategoriesComponent',
  data() {
    return {
      creating: false,
      loading: true,
      saving: false,
      deleting: false,
      addItem: {},
      addMenu: false,
      addForm: false,
      editDialog: false,
      editItem: null,
      editForm: false,
      items: [],
      headers: [
        { text: 'Navn', value: 'title', width: 300 },
        { text: 'Organisatorisk', value: 'organizational' },
        { text: 'Sortering', value: 'sortOrder', align: 'right' },
        { text: 'Actions', value: 'actions', width: 100, align: 'right', sortable: false }
      ]
    }
  },
  mounted() {
    this.getItems();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = null;
      }
    }
  },
  methods: {
    clear: function() {
      this.addItem = {};
      this.addMenu = false;
    },
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get(baseUrl, {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
        this.$emit('loaded', data);
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    },
    create: async function() {
      this.creating = true;

      const model = {
        ...this.addItem,
        customerId: this.$route.params.customerId
      };

      try {
        const { data } = await this.$api.post(baseUrl, model);
        
        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Kategori oprettet' });
        this.addMenu = false;
        this.addItem = {};
        this.getItems();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },
    save: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch(`${baseUrl}/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Kategori opdateret' });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    deleteItem: async function(id) {
      this.deleting = true;

      try {
        const { data } = await this.$api.delete(`${baseUrl}/${id}`);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Kategori slettet' });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.deleting = false;
      }
    },
    onEdit: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    }
  }
}
</script>